import { Card, CardHeader, CardTitle, CardContent, cn } from '@airclou/ui'
import { formatBytes } from '@airclou/utils'
import { useTranslation } from 'react-i18next'
import type { OverviewStats } from '../../api/overview'

export interface StorageUsageCardProps {
  storage: OverviewStats['usage']['storage']
  /** Whoever can buy sees the upgrade path; everyone else is told to ask their admin (T5). */
  canBuy: boolean
  onUpgrade: () => void
}

/**
 * Used storage (files + 5 × database), on every tier
 * (docs/plans/storage-quota-and-fair-use-plan.md §4.7). Free shows used of its
 * quota; paid shows the used figure beside "Unlimited (fair use)". The trash
 * part and the date it frees sit under it, so an admin who deleted tickets
 * knows why the number has not dropped yet.
 */
export function StorageUsageCard({ storage, canBuy, onUpgrade }: StorageUsageCardProps) {
  const { t, i18n } = useTranslation('dashboard')
  const limited = storage.limit !== null && storage.limit !== 'unlimited' ? storage.limit : null

  let body
  if (storage.used === null) {
    body = <p className="text-sm text-muted-foreground">{t('overview.storageUnknown')}</p>
  } else if (limited) {
    const percentage = Math.min(storage.percentage ?? 0, 100)
    const band = storage.band
    body = (
      <>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">
            {t('overview.storageUsedOf', { used: formatBytes(storage.used), quota: formatBytes(limited.quota) })}
          </span>
          <span className="text-sm text-muted-foreground">{Math.round(storage.percentage ?? 0)}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className={cn(
              'h-2 rounded-full transition-all',
              // Status ramp: these colours mean "nearly full" and "full", not brand.
              // eslint-disable-next-line airclou/no-hardcoded-theme-colors -- status ramp, explicit dark steps on every arm
              band === 'nearly_full' && 'bg-yellow-600 dark:bg-yellow-500',
              // eslint-disable-next-line airclou/no-hardcoded-theme-colors -- status ramp, explicit dark steps on every arm
              (band === 'full' || band === 'ceiling') && 'bg-red-600 dark:bg-red-500',
              band === 'under' && 'bg-primary'
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>
        {(band === 'nearly_full' || band === 'full' || band === 'ceiling') && (
          <p className="mt-2 text-sm text-muted-foreground">
            {band === 'nearly_full' && t('overview.storageNearlyFull')}
            {/* Until the rule is switched on (S15) nothing is refused, so the
                text says only that the organization is over its quota. */}
            {band !== 'nearly_full' && !storage.enforced && t('overview.storageOverQuota')}
            {band === 'full' && storage.enforced && t('overview.storageFull', { ceiling: formatBytes(limited.ceiling) })}
            {band === 'ceiling' && storage.enforced && t('overview.storageCeiling')}{' '}
            {canBuy ? (
              <button type="button" onClick={onUpgrade} className="text-primary hover:underline">
                {t('overview.upgradePlan')}
              </button>
            ) : (
              t('overview.storageAskAdmin')
            )}
          </p>
        )}
      </>
    )
  } else {
    body = (
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">
          {t('overview.storageUsed', { used: formatBytes(storage.used) })}
        </span>
        <span className="text-sm text-muted-foreground">{t('overview.storageUnlimitedFairUse')}</span>
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t('overview.storage')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {body}
        {storage.files !== null && storage.messages !== null && (
          <p className="text-xs text-muted-foreground">
            {t('overview.storageParts', { files: formatBytes(storage.files), messages: formatBytes(storage.messages) })}
          </p>
        )}
        {storage.trash !== null && storage.trash > 0 && (
          <p className="text-xs text-muted-foreground">
            {storage.nextPurgeAt
              ? t('overview.storageTrash', {
                  trash: formatBytes(storage.trash),
                  date: new Date(storage.nextPurgeAt).toLocaleDateString(i18n.language),
                })
              : t('overview.storageTrashNoDate', { trash: formatBytes(storage.trash) })}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
